import React, {useState} from 'react';
import API from '../../../../app/api';
import {setSnackbar} from '../../../../store/reducers/globals';
import {useDispatch, useSelector} from 'react-redux';
import {AgyleTable, createAgyleTableColumn} from '../../../../components/table/AgyleTable';
import MiniFormDialog from '../../../../components/modal/miniFormDialog';
import useOpenState from '../../../../hooks/useOpenState';
import {useTranslation} from 'react-i18next';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import makeStyles from '@mui/styles/makeStyles';
import useDict from '../../../../hooks/useDict';
import AgyleDictionaryField from '../../../../components/ui/selector/agyleDictionaryField';
import {filterOptions} from '../../../../controls/agyle/ecds';
import {Chip} from '@mui/material';
import _ from 'lodash';
import useWardDict from '../../../../hooks/useWardDict';
import AgyleSwitch from '../../../../components/ui/switch/agyleSwitch';
import {agyleAssessmentList, reloadAssessmentList} from '../../../../store/reducers/dicts';
import {AGE_DICT} from './assessmentUtil';

const COLUMNS_TABLE = [
    createAgyleTableColumn('no', 'No.', 1, null, 'count', 'notSort', 'medium', 'center', true, false, null, null, null),
    createAgyleTableColumn('description', 'Description', 2, 'description', 'string', 'notSort', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('spec', 'Specialty', 3, 'specialties', 'specialties', 'notSort', 'medium', 'center', true, false, null, null, null),
    // createAgyleTableColumn('clinics', 'Clinic', 4, 'clinics', 'clinics', 'notSort', 'medium', 'center', true, false, null, null, null),
    createAgyleTableColumn('clinics', 'Location', 4, 'clinics', 'clinics', 'notSort', 'medium', 'center', true, false, null, null, null),
    createAgyleTableColumn('ageRestriction', 'Age Restriction', 5, 'ageRestriction', 'array', 'notSort', 'medium', 'center', true, false, null, null, null),
    createAgyleTableColumn('active', 'Active', 5, 'active', 'booleanCheckbox', 'notSort', 'medium', 'center', true, false, null, null, null),
    createAgyleTableColumn('action', 'Action', 7, null, 'action', 'notSort', 'medium', 'right', true, true, null, null),
];

const useStyles = makeStyles((theme) => ({
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    },
}));

const AssessmentList = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const [isEditOpen, openEdit, closeEdit] = useOpenState(false, true);
    const [selectedForm, setSelectedForm] = useState(null);

    // Dict
    const [specialties] = useDict('Specialties', '', 'relevent eq true');
    const [trudWardList] = useWardDict();
    const assessmentList = useSelector(agyleAssessmentList);

    const handleEditItem = (item) => {
        setSelectedForm(item);
        openEdit();
    };

    const handleCloseEditItem = () => {
        closeEdit();
        setSelectedForm(null);
    };

    const handleSaveChanges = () => {
        let clone = _.cloneDeep(selectedForm);

        // Clear object
        delete clone.__version;
        delete clone.__type;
        delete clone.__href;

        API.updateAgyleAssessment(clone)
            .then(res => {
                dispatch(setSnackbar({type: 'success', message: 'The data has been saved successfully.'}));
            })
            .catch(e => {
                console.error(e);
                dispatch(setSnackbar({type: 'error', message: 'Data was not saved successfully.'}));
            })
            .finally(() => {
                dispatch(reloadAssessmentList());
                handleCloseEditItem();
            });
    };

    const renderWardList = (props, option) => {
        return (
            <li {...props} key={option?.__id}>
                <Chip label={option?.wardCode || 'UNK'} sx={{mr: 1, minWidth: 80}}/>
                <span className={classes.ward}>
                    {_.startCase(option?.wardName?.toLowerCase())}
                </span>
            </li>
        );
    };

    const handleChange = (name, val) => {
        setSelectedForm(prevState => ({
            ...prevState,
            [name]: val.map(v => v.__id),
        }));
    };

    const getVal = (type) => {
        let dict;
        // eslint-disable-next-line default-case
        switch (type) {
            case 'specialties':
                dict = specialties;
                break;
            case 'clinics':
                dict = trudWardList;
                break;
            case 'ageRestriction':
                dict = AGE_DICT;
                break;
        }
        const values = [];
        if (dict) {
            _.forEach(selectedForm?.[type], v => {
                const findVal = _.find(dict, {__id: v});
                if (findVal) values.push(findVal);
            });
        }
        return values;
    };

    const renderEditBody = () => {

        return (
            <div className={classes.form}>
                <AgyleTextField
                    label={t('Form description')}
                    value={selectedForm?.description || ''}
                    disabled
                    fullWidth
                    noBorder={false}
                    withNewBackground
                    size="large"
                />

                <AgyleDictionaryField
                    label={t('Form specialties')}
                    options={specialties}
                    isMultiple
                    onChange={(_e, val) => handleChange('specialties', val)}
                    value={getVal('specialties')}
                    getOptionLabel={option => option.__description}
                    noBorder={false}
                    withNewBackground
                    size="large"
                />

                <AgyleDictionaryField
                    // label={t('Form clinic')}
                    label={t('Form location')}
                    options={trudWardList}
                    isMultiple
                    onChange={(_e, val) => handleChange('clinics', val)}
                    value={getVal('clinics')}
                    getOptionLabel={option => option.wardName}
                    renderLocalOption={renderWardList}
                    filterOptions={(opt, state) => filterOptions(opt, state, ['wardCode', 'wardName'])}
                    noBorder={false}
                    withNewBackground
                    size="large"
                />

                <AgyleDictionaryField
                    label={t('Form age restriction')}
                    options={AGE_DICT}
                    isMultiple
                    onChange={(_e, val) => handleChange('ageRestriction', val)}
                    value={getVal('ageRestriction')}
                    noBorder={false}
                    withNewBackground
                    size="large"
                />

                <AgyleSwitch
                    label={t('Form active')}
                    name={'defaultConsultantToMe'}
                    value={selectedForm?.active || false}
                    onChange={() => setSelectedForm(prev => ({...prev, active: !selectedForm?.active}))}
                />
            </div>
        );
    };

    const renderList = () => {
        return (
            <div>
                <AgyleTable
                    data={assessmentList}
                    columns={COLUMNS_TABLE}
                    disableFilter
                    editItem={handleEditItem}
                />
            </div>
        );
    };

    return (
        <>
            {renderList()}

            {(isEditOpen && selectedForm) &&
                <MiniFormDialog
                    open={isEditOpen}
                    onClose={handleCloseEditItem}
                    title={t('Edit form characteristic')}
                    onSave={handleSaveChanges}
                >
                    {renderEditBody()}
                </MiniFormDialog>
            }
        </>
    );
};

export default AssessmentList;